/**
 * Client-side auth state — Zustand store + side-effecting actions.
 *
 * Single source of truth the UI reads via `useAuthStore`. Token storage is
 * delegated to the API client's pluggable `tokenStore`, so the same store
 * works in browser + Capacitor without modification.
 */
import { create } from 'zustand';
import type {
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  User,
} from '@draftedge/api-client';
import { api } from './apiClient';

export interface TokenBalances {
  balance: number;
  lifetime: number;
  spent: number;
}

interface AuthState {
  user: User | null;
  tokens: TokenBalances | null;
  isAuthenticated: boolean;
  /** True during the initial `bootstrap()` call OR an in-flight login. */
  isLoading: boolean;
  /** True only during the very first bootstrap so route guards can wait. */
  isBootstrapping: boolean;
  error: string | null;
  bootstrap: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  /** Cross-domain SSO from draftedge.com — exchange a handoff token for a session. */
  ssoLogin: (token: string) => Promise<void>;
  register: (payload: RegisterRequest) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  clearError: () => void;
}

function messageFromError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  isBootstrapping: true,
  error: null,

  /**
   * On app start: if a token is already in storage, hit /auth/me to confirm
   * it is still valid. Any failure clears local auth — silently. The router
   * then sends the user to /login.
   */
  bootstrap: async () => {
    try {
      const existing = await api.tokenStore.get();
      if (!existing) {
        set({ isBootstrapping: false });
        return;
      }
      const me = await api.auth.me();
      set({
        user: me.user,
        tokens: me.tokens ?? null,
        isAuthenticated: true,
        isBootstrapping: false,
        error: null,
      });
    } catch {
      // 401 already wiped the token via the response interceptor; just
      // reset local state.
      await api.tokenStore.clear();
      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isBootstrapping: false,
      });
    }
  },

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const resp = await api.auth.login(credentials);
      // Pull tokens via /me — login response carries user but not token balances.
      let tokens: TokenBalances | null = null;
      try {
        const me = await api.auth.me();
        tokens = me.tokens ?? null;
        set({
          user: me.user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch {
        // /me failed but login succeeded — still mark authed using the
        // payload from /login so the user sees their dashboard.
        set({
          user: resp.user,
          tokens: null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      }
    } catch (err: unknown) {
      const msg = messageFromError(err, 'Login failed. Please try again.');
      set({ isLoading: false, error: msg });
      throw err;
    }
  },

  ssoLogin: async (token) => {
    set({ isLoading: true, error: null });
    try {
      const resp = await api.auth.sso(token);
      // Mirror `login`: the SSO response carries the user; pull balances via /me.
      try {
        const me = await api.auth.me();
        set({
          user: me.user,
          tokens: me.tokens ?? null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch {
        set({
          user: resp.user,
          tokens: null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      }
    } catch (err: unknown) {
      const msg = messageFromError(err, 'Single sign-on failed. Please sign in.');
      set({ isLoading: false, error: msg });
      throw err;
    }
  },

  register: async (payload) => {
    set({ isLoading: true, error: null });
    try {
      const resp = await api.auth.register(payload);
      set({ isLoading: false });
      return resp;
    } catch (err: unknown) {
      const msg = messageFromError(err, 'Registration failed.');
      set({ isLoading: false, error: msg });
      throw err;
    }
  },

  logout: async () => {
    // Optimistic: flip auth state immediately so the UI updates without
    // waiting on the network round-trip (no dead "Logout" button). The token
    // is still in the store here, so the call below can still invalidate it
    // server-side before we clear it.
    set({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    if (get().isBootstrapping) {
      set({ isBootstrapping: false });
    }
    try {
      await api.auth.logout();
    } catch {
      // Ignore — local state is already cleared.
    }
    await api.tokenStore.clear();
  },

  clearError: () => set({ error: null }),
}));

// Subscribe to the global "unauthorized" event the API client emits on 401s
// so any background request can force the user back to a logged-out state.
if (typeof window !== 'undefined') {
  window.addEventListener('draftedge:unauthorized', () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      // A mid-session 401 means the token expired/was revoked. Flip to
      // logged-out AND leave a friendly message so the login page can explain
      // why the user landed there (instead of an unexplained empty form).
      useAuthStore.setState({
        user: null,
        tokens: null,
        isAuthenticated: false,
        error: 'Your session expired. Please sign in again.',
      });
    }
  });
}
